import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LogOut, DollarSign, Building2, Users, ShoppingBag, TrendingUp, Trophy, UserCog, Loader2, FlaskConical, Settings2, Wrench, CheckCircle2, Swords, Wand2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import CompanyManageDialog from "@/components/CompanyManageDialog";
import BuildBadge from "@/components/BuildBadge";
import { POSTER_CLIP, POSTER_CLIP_SM, PosterStripes, PosterGrain, PosterRibbon } from "@/components/PosterFX";

export default function PlatformAdmin() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [impersonating, setImpersonating] = useState(null); // company_id while in-flight
  const [includeDemo, setIncludeDemo] = useState(false);
  const [manageCompany, setManageCompany] = useState(null); // currently-managed company row
  const [seeding, setSeeding] = useState(false);

  const reload = () => {
    const params = { include_demo: includeDemo };
    api.get("/admin/stats", { params }).then((r) => setStats(r.data));
    api.get("/admin/companies", { params }).then((r) => setCompanies(r.data));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDemo]);

  const impersonate = async (company) => {
    setImpersonating(company.id);
    try {
      await api.post(`/admin/impersonate/${company.id}`);
      await refresh();
      toast.success(`Signed in as ${company.owner_name}`);
      navigate("/dashboard");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not impersonate";
      toast.error(typeof detail === "string" ? detail : "Could not impersonate");
      setImpersonating(null);
    }
  };

  const seedDemo = async () => {
    if (seeding) return;
    if (!window.confirm("Create / refresh the ABC Auto Group demo account?\n\nThis wipes and regenerates the demo company with 10 reps and 180 days of fresh sales data ending today. Login: demo@salesarenahq.com")) return;
    setSeeding(true);
    try {
      const r = await api.post("/admin/seed-demo");
      const d = r.data || {};
      toast.success(`Demo ready: ${d.company_name} — ${d.reps} reps, ${(d.sales_created || 0).toLocaleString()} sales over ${d.window_days} days.`);
      setIncludeDemo(true);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not seed demo account";
      toast.error(typeof detail === "string" ? detail : "Could not seed demo account");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <BuildBadge />
      <header className="border-b border-[#27272A] bg-[#0A0A0A]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="admin-brand">
            <div className="w-8 h-8 bg-[#E13C32] flex items-center justify-center rounded-sm">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-logo leading-none whitespace-nowrap">SALES<span className="text-[#178AE5]">ARENA HQ</span></div>
              <div className="font-label text-[10px] text-[#E13C32]">PLATFORM CONSOLE</div>
            </div>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 px-3 py-2 hover:bg-[#121212] rounded-sm" data-testid="admin-user-menu">
                <div className="w-9 h-9 bg-[#E13C32] flex items-center justify-center rounded-full font-display">{user?.name?.[0]}</div>
                <span className="hidden md:inline text-sm">{user?.email}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#121212] border-[#27272A] text-white w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#27272A]" />
              <DropdownMenuItem
                data-testid="admin-logout-button"
                onClick={async () => { await logout(); navigate("/"); toast.success("Logged out"); }}
                className="cursor-pointer hover:bg-[#0A0A0A] focus:bg-[#0A0A0A]"
              >
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 mb-3 px-2 py-0.5 border border-[#E13C32]/50 bg-[#E13C32]/10 rounded-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E13C32] animate-pulse" />
              <span className="font-label text-[9px] tracking-[0.4em] text-[#E46B5A]">PLATFORM CONSOLE · LIVE</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl mb-2" data-testid="admin-page-title">PLATFORM CONSOLE</h1>
            <p className="text-[#A1A1AA]">
              Every company. Every line. Every rep.
              {!includeDemo && (
                <span className="ml-2 text-xs text-[#A1A1AA]/70">
                  (Demo accounts hidden — toggle to show)
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
            <Button
              onClick={seedDemo}
              disabled={seeding}
              className="bg-[#178AE5] hover:bg-[#1277c9] text-white rounded-sm"
              data-testid="seed-demo-button"
            >
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {seeding ? "Seeding…" : "Seed Demo Account"}
            </Button>
            <label
              htmlFor="demo-toggle"
              className="flex items-center gap-3 px-4 py-2.5 border border-[#27272A] bg-[#121212] rounded-sm cursor-pointer hover:border-[#3F3F46] transition-colors"
              data-testid="demo-toggle-label"
            >
              <FlaskConical className="w-4 h-4 text-[#A1A1AA]" />
              <span className="text-sm text-white">Show demo/test accounts</span>
              <Switch
                id="demo-toggle"
                checked={includeDemo}
                onCheckedChange={setIncludeDemo}
                data-testid="demo-toggle-switch"
            />
            </label>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatTile label="MRR" value={`$${stats?.mrr?.toLocaleString() || 0}`} icon={<DollarSign className="w-5 h-5" />} color="#34C759" testid="stat-mrr" />
          <StatTile label="COMPANIES" value={stats?.total_companies || 0} icon={<Building2 className="w-5 h-5" />} color="#178AE5" testid="stat-companies" />
          <StatTile label="USERS" value={stats?.total_users || 0} icon={<Users className="w-5 h-5" />} color="#178AE5" testid="stat-users" />
          <StatTile label="SALES LOGGED" value={stats?.total_sales || 0} icon={<ShoppingBag className="w-5 h-5" />} color="#E13C32" testid="stat-sales" />
          <StatTile label="LINES SOLD" value={(stats?.total_volume || 0).toLocaleString()} icon={<TrendingUp className="w-5 h-5" />} color="#E13C32" testid="stat-gmv" />
        </div>

        {/* Maintenance panel — one-shot migrations for platform-wide issues */}
        <MaintenancePanel />

        {/* AI quiz generation usage */}
        <AiUsagePanel />

        {/* Companies table */}
        <div
          className="relative border-2 bg-[#121212] overflow-hidden"
          style={{
            borderColor: "#178AE555",
            boxShadow: "0 0 22px rgba(23,138,229,0.14), inset 0 0 22px rgba(0,0,0,0.5)",
            clipPath: POSTER_CLIP,
          }}
          data-testid="companies-table"
        >
          <PosterStripes color="#178AE5" size={100} opacity={0.4} />
          <PosterGrain opacity={0.04} />
          <PosterRibbon color="#178AE5">★ COMPANIES ROSTER ★</PosterRibbon>
          <div className="relative hidden md:grid grid-cols-12 gap-4 px-6 pt-8 pb-3 border-b border-[#178AE5]/25 font-label text-xs text-[#A1A1AA]">
            <div className="col-span-3">COMPANY</div>
            <div className="col-span-3">OWNER</div>
            <div className="col-span-1">PLAN</div>
            <div className="col-span-1 text-center">USERS</div>
            <div className="col-span-1 text-center">SALES</div>
            <div className="col-span-1 text-right">MRR</div>
            <div className="col-span-2 text-right">ACTIONS</div>
          </div>
          {companies.length === 0 ? (
            <div className="relative p-12 pt-14 text-center text-[#A1A1AA]">No companies signed up yet.</div>
          ) : (
            <div className="relative">
              {companies.map((c) => (
              <div key={c.id} className="grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-4 px-4 md:px-6 py-4 border-b border-white/5 last:border-0 items-center hover:bg-white/[0.02] transition-colors" data-testid={`company-row-${c.id}`}>
                <div className="col-span-2 md:col-span-3 min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    <span className="truncate">{c.name}</span>
                    {c.is_demo && (
                      <span
                        className="text-[10px] font-label px-1.5 py-0.5 bg-[#FFC832]/10 text-[#FFC832] border border-[#FFC832]/30 rounded-sm flex-shrink-0"
                        data-testid={`demo-badge-${c.id}`}
                      >
                        DEMO
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#A1A1AA] font-mono">{c.id.slice(0, 8)}</div>
                </div>
                <div className="col-span-2 md:col-span-3 min-w-0">
                  <div className="text-sm truncate">{c.owner_name}</div>
                  <div className="text-xs text-[#A1A1AA] truncate">{c.owner_email}</div>
                </div>
                <div className="col-span-1 md:col-span-1">
                  <span className={`text-xs font-label px-2 py-1 rounded-sm ${c.subscription_status === "active" ? "bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30" : "bg-[#27272A] text-[#A1A1AA]"}`}>
                    {c.plan_label}
                  </span>
                </div>
                <div className="hidden md:block col-span-1 text-center font-mono">{c.user_count}</div>
                <div className="hidden md:block col-span-1 text-center font-mono">{c.sales_count}</div>
                <div className="col-span-1 md:col-span-1 text-right font-mono text-[#34C759] text-xs">${c.monthly_revenue}/mo</div>
                <div className="col-span-2 md:col-span-2 flex justify-end gap-2">
                  <button
                    onClick={() => setManageCompany(c)}
                    className="flex items-center gap-1.5 text-xs font-label px-3 py-1.5 bg-[#27272A] hover:bg-[#3F3F46] text-white border border-[#27272A] hover:border-white/30 rounded-sm transition-colors"
                    data-testid={`manage-${c.id}`}
                    title="Grant tier, freeze, extend trial, send email…"
                  >
                    <Settings2 className="w-3 h-3" />
                    Manage
                  </button>
                  <button
                    onClick={() => impersonate(c)}
                    disabled={impersonating === c.id}
                    className="flex items-center gap-1.5 text-xs font-label px-3 py-1.5 bg-[#178AE5]/10 text-[#178AE5] hover:bg-[#178AE5] hover:text-white border border-[#178AE5]/30 hover:border-[#178AE5] rounded-sm transition-colors disabled:opacity-50"
                    data-testid={`impersonate-${c.id}`}
                    title={`Sign in as ${c.owner_name}`}
                  >
                    {impersonating === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCog className="w-3 h-3" />}
                    Login
                  </button>
                </div>
              </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {manageCompany ? (
        <CompanyManageDialog
          company={manageCompany}
          open={!!manageCompany}
          onClose={() => setManageCompany(null)}
          onChange={() => { reload(); }}
        />
      ) : null}
    </div>
  );
}

/** Per-company AI quiz generation usage — counts, estimated cost, monthly reset. */
function AiUsagePanel() {
  const [data, setData] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = () => api.get("/admin/ai-usage").then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  useEffect(() => { load(); }, []);

  const reset = async (row) => {
    if (!window.confirm(`Reset ${row.company_name}'s AI usage for this month back to 0/${row.cap}?`)) return;
    setResetting(row.company_id);
    try {
      await api.post(`/admin/ai-usage/${row.company_id}/reset`);
      toast.success(`${row.company_name} reset to 0/${row.cap}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); } finally { setResetting(null); }
  };

  const rows = data?.rows || [];
  const active = rows.filter((r) => r.all_time > 0);

  return (
    <div
      className="relative mb-10 border-2 bg-[#121212] p-5 pt-7 overflow-hidden"
      style={{
        borderColor: "#C58AF955",
        boxShadow: "0 0 22px rgba(197,138,249,0.14), inset 0 0 22px rgba(0,0,0,0.5)",
        clipPath: POSTER_CLIP,
      }}
      data-testid="ai-usage-panel"
    >
      <PosterStripes color="#C58AF9" size={100} opacity={0.5} />
      <PosterGrain opacity={0.05} />
      <PosterRibbon color="#C58AF9">★ AI QUIZ GENERATOR ★</PosterRibbon>
      <div className="relative flex items-center gap-2 mb-1">
        <Wand2 className="w-4 h-4 text-[#C58AF9]" />
        <div className="font-label text-xs tracking-widest text-white/70">USAGE {data?.month ? `(${data.month})` : ""}</div>
      </div>
      <p className="relative text-[11px] text-[#A1A1AA] mb-4">
        Each company gets {data?.cap ?? 30} generations/month on your universal key. Cost column is an estimate (~${data?.est_cost_per_generation ?? 0.005}/generation) — exact spend is in your Emergent profile.
      </p>
      {data === null ? (
        <div className="relative py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-[#A1A1AA]" /></div>
      ) : active.length === 0 ? (
        <div className="relative py-3 text-center text-xs text-[#A1A1AA]" data-testid="ai-usage-empty">No AI generations yet this platform-wide.</div>
      ) : (
        <div className="relative overflow-x-auto">
          <div className="hidden md:grid grid-cols-12 gap-3 px-3 py-2 border-b border-[#27272A] font-label text-[10px] text-[#A1A1AA]">
            <div className="col-span-4">COMPANY</div>
            <div className="col-span-2 text-center">THIS MONTH</div>
            <div className="col-span-2 text-center">ALL-TIME</div>
            <div className="col-span-2 text-right">EST. COST</div>
            <div className="col-span-2 text-right">ACTIONS</div>
          </div>
          {active.map((r) => (
            <div key={r.company_id} className="grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-3 px-3 py-2.5 border-b border-[#27272A] last:border-0 items-center"
              data-testid={`ai-usage-row-${r.company_id}`}>
              <div className="col-span-2 md:col-span-4 min-w-0">
                <div className="text-sm truncate">{r.company_name}</div>
                {r.last_at && <div className="text-[10px] text-[#A1A1AA]">last used {new Date(r.last_at).toLocaleDateString()}</div>}
              </div>
              <div className="md:col-span-2 md:text-center font-mono text-sm">
                <span className={r.month_count >= r.cap ? "text-[#E13C32]" : r.month_count > r.cap * 0.8 ? "text-[#FFC832]" : "text-white"}>
                  {r.month_count}/{r.cap}
                </span>
              </div>
              <div className="md:col-span-2 md:text-center font-mono text-sm text-white/70">{r.all_time}</div>
              <div className="md:col-span-2 md:text-right font-mono text-xs text-[#34C759]">~${r.est_cost.toFixed(2)}</div>
              <div className="col-span-2 md:col-span-2 flex md:justify-end">
                <button onClick={() => reset(r)} disabled={resetting === r.company_id || r.month_count === 0}
                  className="flex items-center gap-1.5 text-[10px] font-label px-2.5 py-1.5 bg-[#27272A] hover:bg-[#3F3F46] text-white rounded-sm transition-colors disabled:opacity-40"
                  data-testid={`ai-usage-reset-${r.company_id}`}>
                  {resetting === r.company_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  Reset month
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon, color, testid }) {
  return (
    <div
      className="relative border-2 bg-[#121212] p-5 overflow-hidden"
      style={{
        borderColor: `${color}55`,
        background: `linear-gradient(160deg, ${color}18, #121212 65%, ${color}08)`,
        boxShadow: `0 0 18px ${color}22, inset 0 0 20px rgba(0,0,0,0.5)`,
        clipPath: POSTER_CLIP_SM,
      }}
      data-testid={testid}
    >
      <PosterStripes color={color} size={64} opacity={0.55} />
      <PosterGrain opacity={0.04} />
      <div className="relative flex items-center justify-between mb-2">
        <span className="font-label text-[#A1A1AA] text-[10px] tracking-widest">{label}</span>
        <span style={{ color, filter: `drop-shadow(0 0 6px ${color}88)` }}>{icon}</span>
      </div>
      <div
        className="relative font-display text-3xl font-black leading-none"
        style={{ color, filter: `drop-shadow(0 0 10px ${color}55)` }}
      >
        {value}
      </div>
    </div>
  );
}

/** Platform-wide one-shot migrations. Idempotent — reads /admin/maintenance/oneshots
 *  to grey out any button that's already been run. */
function MaintenancePanel() {
  const [ranMap, setRanMap] = useState(null); // { key: {ran_at, affected, ran_by} } | null while loading
  const [running, setRunning] = useState(null); // key currently in-flight

  useEffect(() => {
    api.get("/admin/maintenance/oneshots").then((r) => setRanMap(r.data || {})).catch(() => setRanMap({}));
  }, []);

  const runUnstickArena = async () => {
    if (!window.confirm(
      "Flip `sales_count_from` to 'created' on EVERY currently pending / active arena challenge across ALL companies.\n\n" +
      "This is idempotent — subsequent clicks are no-ops.\n\nProceed?"
    )) return;
    setRunning("unstick_arena_scf_2026_02");
    try {
      const r = await api.post("/admin/maintenance/unstick-arena-challenges");
      if (r.data.already_ran) {
        toast.message(`Already ran on ${new Date(r.data.ran_at).toLocaleString()} — no-op.`);
      } else {
        toast.success(`Fixed ${r.data.affected} challenge(s).`);
      }
      const r2 = await api.get("/admin/maintenance/oneshots");
      setRanMap(r2.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setRunning(null);
    }
  };

  const arenaRun = ranMap?.unstick_arena_scf_2026_02;

  return (
    <div
      className="relative mb-10 border-2 bg-[#121212] p-5 pt-7 overflow-hidden"
      style={{
        borderColor: "#FFC83255",
        boxShadow: "0 0 22px rgba(255,200,50,0.14), inset 0 0 22px rgba(0,0,0,0.5)",
        clipPath: POSTER_CLIP,
      }}
      data-testid="maintenance-panel"
    >
      <PosterStripes color="#FFC832" size={100} opacity={0.5} />
      <PosterGrain opacity={0.05} />
      <PosterRibbon color="#FFC832">★ MAINTENANCE ★</PosterRibbon>
      <div className="relative flex items-center gap-2 mb-1">
        <Wrench className="w-4 h-4 text-[#FFC832]" />
        <div className="font-label text-xs tracking-widest text-white/70">ONE-SHOT MIGRATIONS</div>
      </div>
      <p className="relative text-[11px] text-[#A1A1AA] mb-4">
        Global fixes. Every run is idempotent and audit-logged. Buttons grey out after a successful run.
      </p>

      <div className="relative grid md:grid-cols-2 gap-3">
        {/* Unstick arena challenges */}
        <div className="flex items-start justify-between gap-3 p-3 border border-[#27272A] rounded-sm bg-[#0A0A0A]">
          <div className="flex items-start gap-3 min-w-0">
            <Swords className="w-5 h-5 text-[#E13C32] mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">Unstick arena challenges (Feb 2026)</div>
              <div className="text-[11px] text-[#A1A1AA] mt-0.5">
                Flips <span className="font-mono">sales_count_from</span> → <span className="font-mono">created</span> on all in-flight duels.
                Retroactively counts pre-accept sales so the 0-0 scores resolve.
              </div>
              {arenaRun ? (
                <div className="text-[10px] text-[#34C759] mt-2 flex items-center gap-1" data-testid="arena-oneshot-status">
                  <CheckCircle2 className="w-3 h-3" />
                  Ran {new Date(arenaRun.ran_at).toLocaleString()} · affected {arenaRun.affected}
                </div>
              ) : null}
            </div>
          </div>
          <Button
            onClick={runUnstickArena}
            disabled={!!arenaRun || running === "unstick_arena_scf_2026_02" || ranMap === null}
            className={`font-label rounded-sm h-9 flex-shrink-0 ${arenaRun ? "bg-[#27272A] text-[#71717A]" : "bg-[#FFC832] hover:bg-[#FFC832] text-black"}`}
            data-testid="oneshot-unstick-arena"
          >
            {running === "unstick_arena_scf_2026_02"
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : arenaRun ? "Already run" : "Run fix"}
          </Button>
        </div>
      </div>
    </div>
  );
}

